import React from 'react';

import every from 'lodash/every';

import {SelectBaseRTK} from 'app/components/general/SelectBase/SelectBaseRTK';
import {useTeams} from 'app/teams/api';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {useCreateTeamModal} from './TeamCreateModal';

import {convertLocationParamsToFetchParams} from '../utils';

import {BasicTeamSerializedType, TeamSerializedType} from 'sweet-types';

import type {
  ActionMeta,
  CreatableProps,
} from 'app/components/general/SweetReactSelect';
import type {InNoTeamsOptionType} from 'app/general/dashboards/DashboardFilterbarTeams';

const teamCt = getContentType('team');

type CommonProps = {
  placeholder: string;
  canCreate: boolean;
  disabled?: boolean;
  refetchDataOnOpen?: boolean;
  params?: Record<string, string | number>;
  addFirstOption?: BasicTeamSerializedType | InNoTeamsOptionType;
  typeIcon?: null | string | ((team: BasicTeamSerializedType) => string | null);
  selectProps?: Partial<
    CreatableProps<BasicTeamSerializedType, BasicTeamSerializedType>
  >;
};

type SingleProps = CommonProps & {
  selected: BasicTeamSerializedType | null | InNoTeamsOptionType;
  onChange: (
    team: BasicTeamSerializedType,
    meta: ActionMeta<BasicTeamSerializedType>
  ) => Promise<unknown>;
};

export const TeamsSelectSingle = ({
  typeIcon = teamCt.icon,
  ...other
}: SingleProps) => (
  <SelectBaseRTK
    isMulti={false}
    typeIcon={typeIcon}
    isRTKFetcher={true}
    useDataHook={useTeams}
    paramsConverter={convertLocationParamsToFetchParams}
    {...other}
  />
);

type MultiProps = CommonProps & {
  selected: Array<BasicTeamSerializedType>;
  onChange: (
    teams: Array<BasicTeamSerializedType>,
    meta: ActionMeta<BasicTeamSerializedType>
  ) => Promise<unknown>;
};

export function TeamsSelectMulti({
  typeIcon = teamCt.icon,
  canCreate,
  onChange,
  selectProps,
  ...other
}: MultiProps) {
  const {show, Modal} = useCreateTeamModal();
  function onCreateTeam(name: string) {
    show({
      object: name,
      action: (team: BasicTeamSerializedType) => {
        return onChange([], {action: 'select-option', option: team});
      },
    });
  }
  function isValidNewOption(
    query: string,
    selected: Array<BasicTeamSerializedType>,
    items: Array<BasicTeamSerializedType>
  ) {
    return (
      query !== '' &&
      every(items, (team) => team.name !== query) &&
      every(selected, (team) => team.name !== query)
    );
  }

  function formatCreateLabel(query: string) {
    return gt(
      'Create %s team',
      <span className="mx-1">
        {'"'}
        <b>{query}</b>
        {'"'}
      </span>
    );
  }

  function isOptionDisabled(
    option: BasicTeamSerializedType | TeamSerializedType | string
  ): boolean {
    // Only disable when we *know* there's a can_edit flag and it's false.
    return (
      typeof option !== 'string' &&
      'can_create' in option &&
      option.can_create === false
    );
  }

  function formatOptionLabel(
    option: BasicTeamSerializedType | TeamSerializedType
  ) {
    if ((option as TeamSerializedType).can_create ?? false) {
      return option.name;
    } else {
      return (
        <span>
          {option.name}{' '}
          <small className="text-muted">({gt('Requires permission')})</small>
        </span>
      );
    }
  }

  return (
    <>
      <SelectBaseRTK
        isMulti={true}
        typeIcon={typeIcon}
        isRTKFetcher={true}
        useDataHook={useTeams}
        paramsConverter={convertLocationParamsToFetchParams}
        selectProps={{
          allowCreateWhileLoading: true,
          onCreateOption: onCreateTeam,
          formatCreateLabel,
          isValidNewOption,
          isOptionDisabled,
          formatOptionLabel,
          ...selectProps,
        }}
        creatable={canCreate}
        onChange={onChange}
        {...other}
      />
      {Modal}
    </>
  );
}
